import i18n from "@/i18n";
/**
 * VuosilomaCorrectionDialog — VUOSILOMA-V1. Audited correction of a
 * manager-recorded opening fact: replace the value or reverse it.
 * The original entry stays in history as REVERSED.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { vlEntryLabel, vlErrorMessage } from "./vuosilomaHelpers";

export function buildCorrectionPayload(entry, { action, value, reason }) {
    const body = { target_entry_id: entry.entry_id, action, reason: reason.trim() };
    if (action !== "REPLACE") return body;
    if (entry.entry_type === "EVIDENCE") {
        if (entry.evidence_kind === "WOULD_HAVE_WORKED") {
            body.minutes = Math.round(Number(String(value).replace(",", ".")) * 60);
        }
    } else if (entry.opening_kind === "PRIOR_YEAR_BALANCE") {
        body.days = Number(String(value).replace(",", "."));
    } else if (["CURRENT_YEAR_SICK_CREDITED", "CURRENT_YEAR_SICK_CALENDAR_DAYS",
        "OPEN_SICK_EPISODE"].includes(entry.opening_kind)) {
        body.days = parseInt(value, 10);
        if (entry.episode_start_date) body.episode_start_date = entry.episode_start_date;
    } else if (entry.opening_kind === "CURRENT_YEAR_EARNED") {
        body.full_months = Number(value);
    } else {
        body.opening_effective_date = value;
    }
    return body;
}

// Only hours are replaceable; other evidence is removed and re-recorded.
const removeOnly = (e) => e.entry_type === "EVIDENCE" && e.evidence_kind !== "WOULD_HAVE_WORKED";

export default function VuosilomaCorrectionDialog({
    entry, onOpenChange, employeeId, onSaved,
}) {
    const [action, setAction] = useState("REPLACE");
    const [value, setValue] = useState("");
    const [reason, setReason] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");

    const entryId = entry?.entry_id;
    const entryRemoveOnly = entry ? removeOnly(entry) : false;
    useEffect(() => {
        if (entryId) { setAction(entryRemoveOnly ? "REVERSE" : "REPLACE"); setValue(""); setReason(""); setError(""); }
    }, [entryId, entryRemoveOnly]);

    if (!entry) return null;
    const isBoundary = entry.opening_kind === "BOUNDARY";
    const canSave = !pending && reason.trim().length >= 3
        && (action === "REVERSE" || value !== "");

    const save = async () => {
        setPending(true);
        setError("");
        try {
            await api.post(`/workforce/employees/${employeeId}/vuosiloma/corrections`,
                buildCorrectionPayload(entry, { action, value, reason }));
            toast.success(i18n.t("wf.vuosilomaCorrectionDialog.correctionSaved", "Correction saved"));
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            setError(vlErrorMessage(err));
        } finally {
            setPending(false);
        }
    };

    return (
        <Dialog open={!!entry} onOpenChange={onOpenChange}>
            <DialogContent data-testid="vl-correction-dialog" className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{entry.entry_type === "EVIDENCE" ? i18n.t("wf.vuosilomaCorrectionDialog.correctEvidence", "Correct evidence") : i18n.t("wf.vuosilomaCorrectionDialog.correctOpeningFact", "Correct opening fact")}</DialogTitle>
                    <DialogDescription data-testid="vl-correction-target">
                        {vlEntryLabel(entry)}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    {!isBoundary && !removeOnly(entry) && (
                        <div className="flex gap-2">
                            <Button size="sm" variant={action === "REPLACE" ? "default" : "outline"}
                                data-testid="vl-correction-action-replace"
                                onClick={() => setAction("REPLACE")}>{i18n.t("wf.vuosilomaCorrectionDialog.replaceValue", "Replace value")}</Button>
                            <Button size="sm" variant={action === "REVERSE" ? "default" : "outline"}
                                data-testid="vl-correction-action-reverse"
                                onClick={() => setAction("REVERSE")}>{i18n.t("wf.vuosilomaCorrectionDialog.removeFact", "Remove fact")}</Button>
                        </div>
                    )}
                    {action === "REPLACE" && (
                        <label className="block">
                            <span className="text-neutral-700">
                                {isBoundary ? i18n.t("wf.vuosilomaCorrectionDialog.newOpeningDate", "New opening date")
                                    : entry.entry_type === "EVIDENCE" ? i18n.t("wf.vuosilomaCorrectionDialog.correctHours", "Correct hours")
                                    : entry.opening_kind === "PRIOR_YEAR_BALANCE"
                                        ? i18n.t("wf.vuosilomaCorrectionDialog.correctRemainingDays", "Correct remaining days")
                                        : entry.opening_kind === "CURRENT_YEAR_SICK_CREDITED"
                                            ? i18n.t("wf.vuosilomaCorrectionDialog.correctSickLeaveDays", "Correct sick-leave days") : i18n.t("wf.vuosilomaCorrectionDialog.correctFullMonths", "Correct full months")}
                            </span>
                            <Input className="mt-1"
                                type={isBoundary ? "date" : "number"}
                                min="0" step={entry.entry_type === "EVIDENCE" ? "0.25" : entry.opening_kind === "PRIOR_YEAR_BALANCE" ? "0.5" : "1"}
                                value={value} data-testid="vl-correction-value"
                                onChange={(e) => setValue(e.target.value)} />
                        </label>
                    )}
                    <label className="block">
                        <span className="text-neutral-700">{i18n.t("wf.vuosilomaCorrectionDialog.reason", "Reason")}</span>
                        <Textarea rows={2} className="mt-1" value={reason}
                            data-testid="vl-correction-reason"
                            onChange={(e) => setReason(e.target.value)} />
                    </label>
                    {error && (
                        <div role="alert" data-testid="vl-correction-error"
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                            {error}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        data-testid="vl-correction-cancel">{i18n.t("common.cancel", "Cancel")}</Button>
                    <Button onClick={save} disabled={!canSave} data-testid="vl-correction-save">
                        {pending ? i18n.t("common.saving", "Saving…") : i18n.t("wf.vuosilomaCorrectionDialog.saveCorrection", "Save correction")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
